import { useCallback } from "react";

import { SaleTypeForTerm, type InputMaybe } from "@/lib/gql/__generated__/graphql";

import type { ForNodeType } from "@/components/features/_model/types/auto-generator-for-view-model";

import type { DateRange } from "@/components/ui/calendar/types/calendar-range";
import type { FormOptionType } from "@/components/ui/form/_types/type";

type Props = {
	saleTypeForTerm: ForNodeType<SaleTypeForTerm>;
	saleStart: ForNodeType<InputMaybe<string> | undefined> | undefined;
	saleEnd: ForNodeType<InputMaybe<string> | undefined> | undefined;
	stayableStart: ForNodeType<InputMaybe<string> | undefined> | undefined;
	stayableEnd: ForNodeType<InputMaybe<string> | undefined> | undefined;

	onSetViewModel: (
		values: {
			key: "saleEnd" | "saleStart" | "saleTypeForTerm" | "stayableEnd" | "stayableStart";
			value: ForNodeType<SaleTypeForTerm> | ForNodeType<InputMaybe<string> | undefined>;
		}[],
	) => void;
};

export const useSalesTerm = ({
	saleTypeForTerm,
	saleStart,
	saleEnd,
	stayableStart,
	stayableEnd,
	onSetViewModel,
}: Props) => {
	const isEdited = saleTypeForTerm.isEdited;
	const hasError = Boolean(
		saleTypeForTerm.error || saleStart?.error || saleEnd?.error || stayableStart?.error || stayableEnd?.error,
	);

	const isLimitedSale = saleTypeForTerm.value === SaleTypeForTerm.Limited;

	const saleTypeForTermOptions = [
		{ id: `saleTypeForTermOptions_0`, label: "常に販売", value: SaleTypeForTerm.Always },
		{ id: `saleTypeForTermOptions_1`, label: "期間を限定して販売", value: SaleTypeForTerm.Limited },
	] satisfies FormOptionType[];

	const onSetSaleTypeForTerm = useCallback(
		(input: string) => {
			const inputToSaleTypeForTerm = input as SaleTypeForTerm;
			if (SaleTypeForTerm.Always === inputToSaleTypeForTerm) {
				onSetViewModel([
					{
						key: "saleTypeForTerm",
						value: {
							isEdited: true,
							value: SaleTypeForTerm.Always,
						},
					},
					{
						key: "saleStart",
						value: {
							isEdited: true,
							value: null,
						},
					},
					{
						key: "saleEnd",
						value: {
							isEdited: true,
							value: null,
						},
					},
					{
						key: "stayableStart",
						value: {
							isEdited: true,
							value: null,
						},
					},
					{
						key: "stayableEnd",
						value: {
							isEdited: true,
							value: null,
						},
					},
				]);
			} else {
				onSetViewModel([
					{
						key: "saleTypeForTerm",
						value: {
							isEdited: true,
							value: inputToSaleTypeForTerm,
						},
					},
					{
						key: "saleStart",
						value: {
							isEdited: true,
							value: "",
						},
					},
					{
						key: "saleEnd",
						value: {
							isEdited: true,
							value: "",
						},
					},
					{
						key: "stayableStart",
						value: {
							isEdited: true,
							value: "",
						},
					},
					{
						key: "stayableEnd",
						value: {
							isEdited: true,
							value: "",
						},
					},
				]);
			}
		},
		[onSetViewModel],
	);

	const onSetSaleTerm = useCallback(
		(input: DateRange) => {
			onSetViewModel([
				{
					key: "saleStart",
					value: {
						isEdited: true,
						value: input.from,
					},
				},
				{
					key: "saleEnd",
					value: {
						isEdited: true,
						value: input.to,
					},
				},
			]);
		},
		[onSetViewModel],
	);

	const onSetStayableTerm = useCallback(
		(input: DateRange) => {
			onSetViewModel([
				{
					key: "stayableStart",
					value: {
						isEdited: true,
						value: input.from,
					},
				},
				{
					key: "stayableEnd",
					value: {
						isEdited: true,
						value: input.to,
					},
				},
			]);
		},
		[onSetViewModel],
	);

	return {
		isEdited,
		hasError,

		isLimitedSale,

		saleTypeForTermOptions,
		saleTypeForTerm,
		onSetSaleTypeForTerm,

		saleStart,
		saleEnd,
		onSetSaleTerm,

		stayableStart,
		stayableEnd,
		onSetStayableTerm,
	};
};
